import React from 'react';
import { SystemConfig, VerificationConfig } from '@/types';
import { ShieldCheck, Smartphone } from 'lucide-react';
import { MobileVerificationPreview } from './settingsShared';

type VerificationToggleKey =
  | 'showName'
  | 'showDate'
  | 'showRef'
  | 'showOutcome'
  | 'showExpiry'
  | 'requireNameConfirmation';

const VERIFICATION_ITEMS: Array<{ k: VerificationToggleKey; l: string; d: string }> = [
  { k: 'requireNameConfirmation', l: 'Verify Patient Name', d: 'Forces manual name entry' },
  { k: 'showName', l: 'Display Name', d: 'Shows patient name after check' },
  { k: 'showDate', l: 'Display Visit Date', d: 'Shows clinical visit timestamp' },
  { k: 'showRef', l: 'Display Cert ID', d: 'Shows unique certificate UUID' },
  { k: 'showExpiry', l: 'Display Validity', d: 'Shows if expired or active' },
  { k: 'showOutcome', l: 'Display Outcome', d: 'Shows Fit/Unfit status' }
];

interface VerificationSectionProps {
  config: SystemConfig;
  onUpdate: (config: SystemConfig) => void;
}

export const VerificationSection: React.FC<VerificationSectionProps> = ({ config, onUpdate }) => {
  const verificationConfig = config.verification as Record<VerificationToggleKey, boolean>;

  return (
    <div className="flex flex-col lg:flex-row gap-12 animate-fade-in p-2">
      <div className="flex-1 space-y-6">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <h4 className="font-bold text-lg mb-2 text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-teal-600" /> Verification Engine
          </h4>
          <p className="text-xs text-slate-500 mb-8 font-medium">
            Control the data patients and third-parties see when scanning certificate QR codes.
          </p>
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                Default Success Message
              </label>
              <textarea
                rows={2}
                className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
                value={config.verification.validMessage}
                onChange={(e) =>
                  onUpdate({
                    ...config,
                    verification: { ...config.verification, validMessage: e.target.value }
                  })
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {VERIFICATION_ITEMS.map((item) => (
                <label
                  key={item.k}
                  className={`flex items-start gap-3 p-4 border rounded-2xl transition-all cursor-pointer group ${verificationConfig[item.k] ? 'bg-teal-50 border-teal-200' : 'bg-white hover:bg-slate-50'}`}
                >
                  <div className="pt-0.5">
                    <input
                      type="checkbox"
                      className="w-5 h-5 text-teal-600 rounded"
                      checked={verificationConfig[item.k]}
                      onChange={(e) =>
                        onUpdate({
                          ...config,
                          verification: { ...config.verification, [item.k]: e.target.checked }
                        })
                      }
                    />
                  </div>
                  <div>
                    <span className="block text-xs font-black text-slate-800 uppercase tracking-tight">
                      {item.l}
                    </span>
                    <span className="block text-[10px] text-slate-400 mt-0.5 font-medium">
                      {item.d}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[320px]">
        <div className="bg-slate-900/5 p-4 rounded-3xl border-2 border-dashed border-slate-200 flex flex-col items-center">
          <div className="flex items-center gap-2 mb-4 text-slate-400">
            <Smartphone className="w-4 h-4" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">Patient View</span>
          </div>
          <MobileVerificationPreview
            config={config.verification as VerificationConfig}
            logoUrl={config.general.logoUrl || ''}
          />
        </div>
      </div>
    </div>
  );
};
